import React from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Button, 
  Card, 
  CardContent, 
  Grid,
  Divider,
  useTheme,
  useMediaQuery,
  Paper
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';

const CampDetails = ({ camps }) => {
  const { handle } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Find the camp with the matching handle
  const camp = camps.find(c => c.handle === handle);
  
  if (!camp) {
    return (
      <Container sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h4">Camp not found</Typography>
        <Button 
          variant="contained" 
          onClick={() => navigate('/')}
          sx={{ mt: 2 }}
        >
          Return to Camp Schedule
        </Button>
      </Container>
    );
  }

  // Group available weeks by status (open, limited spots, etc.)
  const availableWeeks = camp.weeks.filter(w => w.available);
  const openWeeks = availableWeeks.filter(w => !w.limitedSpots);
  const limitedWeeks = availableWeeks.filter(w => w.limitedSpots);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Back button */}
      <Button 
        variant="outlined" 
        onClick={() => navigate('/')}
        sx={{ mb: 3 }}
      >
        ← Back to Schedule
      </Button>

      {/* Main content */}
      <Grid container spacing={4}>
        {/* Left column - Image and details */}
        <Grid item xs={12} md={5}>
          <Box sx={{ position: 'sticky', top: 20 }}>
            {camp.imageUrl && (
              <Box 
                component="img"
                src={camp.imageUrl}
                alt={camp.imageAlt}
                sx={{
                  width: '100%',
                  borderRadius: 2,
                  mb: 2,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
              />
            )}
            
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Ages: 10-12 • {camp.tags.includes('beginner') ? 'Beg-Int' : 'Beg-Adv'}
                </Typography>
                
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="h6" gutterBottom>
                  Price: ${parseFloat(camp.price).toFixed(2)} USD
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Grid>
        
        {/* Right column - Description and available dates */}
        <Grid item xs={12} md={7}>
          <Typography variant="h3" component="h1" gutterBottom>
            {camp.title}
          </Typography>
          
          <Typography variant="body1" paragraph sx={{ mb: 4 }}>
            {camp.description}
          </Typography>
          
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h5">Available Dates</Typography>
              <Button variant="text" onClick={() => navigate('/')}>
                Close Dates
              </Button>
            </Box>
            
            {/* Available dates cards */}
            <Box sx={{ mb: 3 }}>
              {availableWeeks.map((week) => {
                // Determine if this week has limited spots
                const hasLimitedSpots = week.limitedSpots;
                const spotStatus = hasLimitedSpots ? `${week.spotsLeft} left` : 'Open';
                const statusColor = hasLimitedSpots ? '#ff9800' : '#4caf50';
                
                return (
                  <Card 
                    key={week.week} 
                    variant="outlined" 
                    sx={{ 
                      mb: 2,
                      borderColor: hasLimitedSpots ? '#ff9800' : '#e0e0e0'
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="h6">
                            {week.month} {week.dates}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                            <Box 
                              sx={{ 
                                width: 12, 
                                height: 12, 
                                borderRadius: '50%', 
                                bgcolor: statusColor,
                                mr: 1
                              }} 
                            />
                            <Typography variant="body2" color="text.secondary">
                              {spotStatus}
                            </Typography>
                          </Box>
                        </Box>
                        <Button 
                          variant="contained" 
                          color="primary"
                          sx={{ 
                            borderRadius: '20px',
                            px: 3
                          }}
                          onClick={() => window.open(`https://${process.env.REACT_APP_SHOPIFY_STORE_URL}/products/${camp.handle}`, '_blank')}
                        >
                          Select Date
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
            
            {availableWeeks.length === 0 && (
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#f5f5f5' }}>
                <Typography variant="h6" color="text.secondary">
                  No dates currently available
                </Typography>
                <Button 
                  variant="outlined" 
                  sx={{ mt: 2 }}
                  onClick={() => window.open(`https://${process.env.REACT_APP_SHOPIFY_STORE_URL}/products/${camp.handle}`, '_blank')}
                >
                  Try online
                </Button>
              </Paper>
            )}
          </Box>
          
          {/* Course info button */}
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button 
              variant="outlined"
              size="large"
              onClick={() => window.open(`https://${process.env.REACT_APP_SHOPIFY_STORE_URL}/products/${camp.handle}`, '_blank')}
              sx={{ 
                borderRadius: '20px',
                px: 4,
                py: 1
              }}
            >
              Course info
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CampDetails;
